
import { Options, Vue } from 'vue-class-component';
import { articlesService, Article, ArticleImage } from '../services/articles.service';
import Markdown from 'vue3-markdown-it';

@Options({
    components: { Markdown }
})
export default class EditArticleView extends Vue {
    article: Partial<Article> = {
        title: '',
        author: '',
        headlineImage: '',
        tags: [],
        markdownContent: '',
        status: 'draft'
    };
    tab = 'edit';
    saving = false;
    publishing = false;
    isNew = true;
    images: ArticleImage[] = [];
    uploading = false;
    uploadProgress = 0;
    isDraggingFile = false;
    isDraggingOverEditor = false;
    dragCounter = 0;

    async created() {
        const id = this.$route.params.id as string;
        if (id) {
            const existing = await articlesService.getArticle(id);
            if (existing) {
                this.article = existing;
                this.isNew = false;
            }
            await this.loadImages();
        }
    }

    async loadImages() {
        const id = this.$route.params.id as string;
        if (id) {
            this.images = await articlesService.getArticleImages(id);
        }
    }

    triggerFileUpload() {
        (this.$refs.fileInput as HTMLInputElement).click();
    }

    async handleFileInputChange(event: Event) {
        const input = event.target as HTMLInputElement;
        if (input.files?.[0]) {
            await this.uploadFile(input.files[0]);
            input.value = '';
        }
    }

    async uploadFile(file: File) {
        if (!file.type.startsWith('image/')) return;
        const id = this.$route.params.id as string;
        if (!id) return;

        this.uploading = true;
        this.uploadProgress = 0;
        try {
            const image = await articlesService.uploadImage(id, file, (pct) => {
                this.uploadProgress = pct;
            });
            this.images.push(image);
            this.insertMarkdownAtCursor(`![${image.filename}](/api/articles/images/${image.id})`);
        } finally {
            this.uploading = false;
        }
    }

    insertMarkdownAtCursor(markdown: string) {
        const textarea = (this.$refs.markdownEditor as any)?.$el?.querySelector('textarea');
        const content = this.article.markdownContent || '';

        if (!textarea) {
            this.article.markdownContent = content + '\n' + markdown;
            return;
        }

        const start = textarea.selectionStart ?? content.length;
        const end = textarea.selectionEnd ?? start;
        this.article.markdownContent = content.slice(0, start) + markdown + content.slice(end);

        this.$nextTick(() => {
            const newPos = start + markdown.length;
            textarea.selectionStart = textarea.selectionEnd = newPos;
            textarea.focus();
        });
    }

    // Page-level drag handlers — tracks file drags from the OS
    onDragEnter(e: DragEvent) {
        if (e.dataTransfer?.types.includes('Files')) {
            this.dragCounter++;
            this.isDraggingFile = true;
        }
    }

    onDragLeave() {
        if (this.isDraggingFile) {
            this.dragCounter--;
            if (this.dragCounter <= 0) {
                this.dragCounter = 0;
                this.isDraggingFile = false;
            }
        }
    }

    async handleDrop(e: DragEvent) {
        this.isDraggingFile = false;
        this.dragCounter = 0;
        this.isDraggingOverEditor = false;

        // Sidebar image drag — insert markdown at cursor
        if (e.dataTransfer?.types.includes('application/x-article-image')) {
            const markdown = e.dataTransfer.getData('text/plain');
            if (markdown) this.insertMarkdownAtCursor(markdown);
            return;
        }

        // File drag from OS — upload each image file
        if (e.dataTransfer?.files.length) {
            for (const file of Array.from(e.dataTransfer.files)) {
                if (file.type.startsWith('image/')) {
                    await this.uploadFile(file);
                }
            }
        }
    }

    // Editor drop zone visual feedback
    onEditorDragEnter(e: DragEvent) {
        if (
            e.dataTransfer?.types.includes('Files') ||
            e.dataTransfer?.types.includes('application/x-article-image')
        ) {
            this.isDraggingOverEditor = true;
        }
    }

    onEditorDragLeave() {
        this.isDraggingOverEditor = false;
    }

    onSidebarImageDragStart(image: ArticleImage, e: DragEvent) {
        const markdown = `![${image.filename}](/api/articles/images/${image.id})`;
        e.dataTransfer?.setData('text/plain', markdown);
        e.dataTransfer?.setData('application/x-article-image', image.id);
    }

    async deleteImage(image: ArticleImage) {
        if (!confirm(`Delete image "${image.filename}"?`)) return;
        await articlesService.deleteImage(image.id);
        this.images = this.images.filter(i => i.id !== image.id);
    }

    async save() {
        this.saving = true;
        try {
            const id = this.$route.params.id as string;
            await articlesService.updateArticle(id, this.article);
        } finally {
            this.saving = false;
        }
    }

    async publish() {
        this.publishing = true;
        try {
            await this.save();
            const id = this.$route.params.id as string;
            await articlesService.publishArticle(id);
            this.article.status = 'published';
        } finally {
            this.publishing = false;
        }
    }

    cancel() {
        this.$router.push({ path: '/admin/articles' });
    }
}
